/**
 * SelectStarExpand — the "Choose columns" affordance shown next to a bare `*`
 * SELECT list. One click replaces the star with the explicit column list it
 * stands for (`starExpansionFor`: the statement's own tables — never an
 * enclosing query's, which a drilled subquery's picker also lists — in the
 * order and number the engine's `*` returns them, a USING / NATURAL join's
 * shared columns once), turning the single opaque `*` chip into per-column
 * chips the user can remove, reorder, or add to. The seed from "Query a
 * table" (`SELECT * FROM t`) lands here first. Off on Oracle when a shared
 * column's name is ambiguous: Oracle forbids the qualifier it would need.
 */
import { Columns3 } from 'lucide-react';
import { useMemo } from 'react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { VqeEditContext } from '../../edit/editContext';
import { expandSelectStar, starExpansionFor } from '../../edit/edits/expandSelectStar';
import { useInScopeColumnPicker } from '../common/useInScopeColumnPicker';
import { VqeTooltip } from '../common/VqeTooltip';
import { vqeCopy } from '../../ux/vqeCopy';

export interface SelectStarExpandProps {
  readonly editContext: VqeEditContext;
  readonly bufferText: string;
  /** Absolute offsets of the `*` select item. */
  readonly starSpan: { readonly start: number; readonly end: number };
}

export function SelectStarExpand({ editContext, bufferText, starSpan }: SelectStarExpandProps) {
  const { track } = useAnalytics();
  const picker = useInScopeColumnPicker(
    editContext,
    `${editContext.documentId}::${editContext.statementId}::star`,
  );

  // A source whose columns are not known (a CTE, a derived table) makes the
  // picker say so — `*` is only expanded when every source is listed.
  const snapshot = editContext.service.getSchemaSnapshot();
  // The service parses a new buffer asynchronously (`onTextChange`), so the
  // first render after an edit — the table picker seeding `SELECT * FROM t` —
  // can carry `bufferText` ahead of the service's own parse. The expansion then
  // finds no statement and reads `unknown`; with only `bufferText` as the key
  // that `unknown` outlived the parse catching up ("Choose columns" stayed off
  // until the next schema pull). The parse it reads is keyed too.
  const parsedBuffer = editContext.service.getParsedBuffer(editContext.documentId);
  const expansion = useMemo(
    () => starExpansionFor(editContext),
    // `bufferText` stands for the statement's text (the buffer it was rendered against).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editContext.service, editContext.documentId, editContext.statementId, bufferText, snapshot, parsedBuffer],
  );
  const unavailable =
    picker.emptyMessage ??
    picker.notice ??
    (expansion.kind === 'unqualifiable'
      ? vqeCopy.picker.starMergedJoin
      : expansion.kind === 'noFullJoin'
        ? vqeCopy.picker.starNoFullJoin
        : undefined);
  const items = expansion.kind === 'columns' ? expansion.items : [];
  const enabled = picker.enabled && items.length > 0 && !unavailable;

  const onClick = () => {
    if (!enabled) return;
    void expandSelectStar({ editContext, starSpan, originalSource: bufferText, items }).then((applied) => {
      if (applied) track('feature:vqe_expand_star', { count: items.length });
    });
  };

  return (
    <VqeTooltip
      content={
        enabled
          ? `Replace * with the ${items.length} columns in scope so you can pick which to keep`
          : unavailable ?? 'Columns load once the schema cache is ready'
      }
    >
      <button
        type="button"
        onClick={onClick}
        disabled={!enabled}
        className="inline-flex h-7 items-center gap-1 rounded px-2 text-xs text-muted-foreground transition-transform hover:bg-accent hover:text-foreground active:scale-[0.96] disabled:opacity-50"
        data-testid="vqe-select-star-expand"
      >
        <Columns3 className="h-3.5 w-3.5" />
        Choose columns
      </button>
    </VqeTooltip>
  );
}
